import { FC, useCallback, useState } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Button } from '@mui/material';

import { setupUserProfile } from '@api/profile';
import Modal from '@components/Modal/Modal';
import { IUserProfileInitialFormValues } from '@interfaces/profile';
import { handleRefreshToken } from '@store/features/auth/token/actions';
import { userDataSelector } from '@store/features/auth/user/selectors';
import useDispatch from '@store/hooks/useDispatch';
import handleLogout from '@utils/handleLogout';
import handleToast from '@utils/handleToast';

import UserProfileInitialForm from './UserProfileInitialForm/UserProfileInitialForm';
import { IUserProfileModalInitialProps } from './UserProfileModalInitial.interface';

import './style.scss';

const handleChangePassword = (): void => {
  if (process.env.REACT_APP_FORGOT_PASS_URL) {
    window.location.href = process.env.REACT_APP_FORGOT_PASS_URL;
  }
};

const UserProfileModalInitial: FC<IUserProfileModalInitialProps> = ({ open }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector(userDataSelector);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpdateUserProfile = useCallback(
    async (values: IUserProfileInitialFormValues): Promise<void> => {
      setIsSubmitting(true);

      try {
        const response = await setupUserProfile({
          organizationId: values.organizationId,
          organizationAccessCode: values.organizationCode,
        });

        if (response.status === 200) {
          handleToast('success', t('common.messages.profileUpdatedSuccessfully'));
          dispatch(handleRefreshToken());
        }
      } catch (_) {
        setIsSubmitting(false);
      }
    },
    [dispatch, t],
  );

  const handleValidateUserProfileInitialForm = useCallback(
    (values: IUserProfileInitialFormValues) => {
      const errors: Partial<Record<keyof IUserProfileInitialFormValues, string | boolean>> = {};

      if (!values.organizationId) errors.organizationId = true;
      if (!values.organizationCode) errors.organizationCode = true;

      return errors;
    },
    [],
  );

  const handlePreventClose = useCallback(() => {
    handleToast('info', t('common.messages.selectOrgId'));
  }, [t]);

  return (
    <Modal
      title={t('userProfile.title')}
      open={open}
      onClose={handlePreventClose}
      rightActionTitle={t('common.next')}
      isRightActionLoading={isSubmitting}
      className="user-profile-modal-initial"
      submitFormId="user-profile-initial-form"
    >
      <Box className="user-profile-modal-initial__email">
        <Box>{user?.email}</Box>
        <Button variant="text" onClick={handleChangePassword}>
          {t('userProfile.changePassword')}
        </Button>
        <Button variant="contained" onClick={handleLogout}>
          {t('common.auth.logout')}
        </Button>
      </Box>

      <Box className="user-profile-modal-initial__info">{t('userProfile.initialInfo')}</Box>

      <Box className="user-profile-modal-initial__description">
        {t('userProfile.initialDescription')}
      </Box>

      <Box className="user-profile-modal-initial__fields">
        <Form
          component={UserProfileInitialForm}
          validate={handleValidateUserProfileInitialForm}
          onSubmit={handleUpdateUserProfile}
        />
      </Box>
    </Modal>
  );
};

export default UserProfileModalInitial;
